import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Phone, Mail, Car, Calendar, Clock, Trash2, RefreshCw, Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BRAND, LOGO_URL } from "@/lib/data";

const STATUS = {
  pending: { label: "Függőben", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  confirmed: { label: "Megerősítve", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  completed: { label: "Elkészült", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  cancelled: { label: "Lemondva", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

const TABS = [
  { v: "all", l: "Összes" },
  { v: "pending", l: "Függőben" },
  { v: "confirmed", l: "Megerősítve" },
  { v: "completed", l: "Elkészült" },
  { v: "cancelled", l: "Lemondva" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings/stats/summary");
      setStats(data);
    } catch (e) {
      console.error("Nem sikerült betölteni a statisztikát:", e);
    }
  }, []);

  const loadBookings = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings", { params: { status } });
      setBookings(data);
    } catch (e) {
      toast.error("Nem sikerült betölteni a foglalásokat");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setMe(data))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    if (me) { loadBookings(tab); loadStats(); }
  }, [me, tab, loadBookings, loadStats]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Kijelentkezési hiba:", e); }
    navigate("/admin/login");
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success("Státusz frissítve");
      loadBookings(tab);
      loadStats();
    } catch (e) {
      toast.error("Nem sikerült frissíteni");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/bookings/${deleteId}`);
      toast.success("Foglalás törölve");
      setDeleteId(null);
      loadBookings(tab);
      loadStats();
    } catch (e) {
      toast.error("Nem sikerült törölni");
    }
  };

  if (!me) return <div className="min-h-screen bg-[#0B0D11]" />;

  return (
    <div className="min-h-screen bg-[#0B0D11]">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0D11]/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt={BRAND.name} className="h-10 w-10 rounded-full" />
            <div>
              <p className="font-display font-bold text-white leading-none">Admin felület</p>
              <p className="text-slate-500 text-xs font-mono mt-0.5">{me.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { loadBookings(tab); loadStats(); }} data-testid="admin-refresh" className="border-white/15 bg-white/5 text-white hover:bg-white/10 gap-2">
              <RefreshCw className="h-4 w-4" /> <span className="hidden sm:inline">Frissítés</span>
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout" className="border-white/15 bg-white/5 text-white hover:bg-brand-red hover:border-brand-red gap-2">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Kilépés</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display font-bold text-white text-2xl sm:text-3xl mb-6">Foglalások</h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            <StatCard testId="stat-total" label="Összes" value={stats.total} active />
            {Object.keys(STATUS).map((k) => (
              <StatCard key={k} testId={`stat-${k}`} label={STATUS[k].label} value={stats[k] || 0} />
            ))}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="mb-6">
          <TabsList className="bg-[#13171F] border border-white/10 flex-wrap h-auto">
            {TABS.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`} className="data-[state=active]:bg-brand-red data-[state=active]:text-white text-slate-400">
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="text-slate-500 py-20 text-center">Betöltés...</div>
        ) : bookings.length === 0 ? (
          <Card className="metal-card rounded-2xl p-16 flex flex-col items-center text-center">
            <Inbox className="h-12 w-12 text-slate-600 mb-4" />
            <p className="text-slate-400">Nincs foglalás ebben a kategóriában.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {bookings.map((b) => (
              <Card key={b.id} data-testid={`booking-row-${b.id}`} className="metal-card rounded-2xl p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="font-display font-bold text-white text-lg">{b.name}</h3>
                    <p className="text-brand-gold text-sm font-medium">{b.service}</p>
                  </div>
                  <Badge className={`${STATUS[b.status]?.cls} border font-mono text-xs`}>{b.status_label}</Badge>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-4">
                  <Info icon={Calendar} text={b.date} />
                  <Info icon={Clock} text={b.time_slot} />
                  <Info icon={Phone} text={<a href={`tel:${b.phone}`} className="hover:text-brand-red">{b.phone}</a>} />
                  {b.email && <Info icon={Mail} text={<a href={`mailto:${b.email}`} className="hover:text-brand-red truncate">{b.email}</a>} />}
                  {b.car_type && <Info icon={Car} text={b.car_type} />}
                </div>

                {b.problem && (
                  <p className="text-slate-400 text-sm bg-[#0B0D11] border border-white/10 rounded-lg p-3 mb-4">{b.problem}</p>
                )}

                <div className="flex items-center gap-2">
                  <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                    <SelectTrigger data-testid={`status-select-${b.id}`} className="bg-[#0B0D11] border-white/15 flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(STATUS).map((k) => (
                        <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid={`delete-btn-${b.id}`}
                    onClick={() => setDeleteId(b.id)}
                    className="border-white/15 bg-white/5 text-red-400 hover:bg-red-500/15 shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="metal-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Foglalás törlése</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Biztosan törli ezt a foglalást? A művelet nem vonható vissza.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/15 text-white hover:bg-white/10">Mégse</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete" className="bg-brand-red hover:bg-[#c11010] text-white">Törlés</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, active, testId }) {
  return (
    <Card data-testid={testId} className={`metal-card rounded-xl p-4 ${active ? "border-brand-red/40" : ""}`}>
      <p className="text-slate-500 text-xs font-mono uppercase mb-1">{label}</p>
      <p className={`font-display font-extrabold text-2xl ${active ? "text-brand-red" : "text-white"}`}>{value}</p>
    </Card>
  );
}

function Info({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-slate-300">
      <Icon className="h-4 w-4 text-slate-500 shrink-0" />
      <span className="truncate">{text}</span>
    </div>
  );
}
